import * as React from 'react';
import { observer } from 'mobx-react-lite';
import toPixelString from '../utils/toPixelString';
import SurfaceConstants from 'owa-calendar-surface-core-styles/lib/SurfaceConstants';
import { getAllDayWellHeight } from 'owa-calendar-surface-options-utils';

import styles from './CalendarAllDayWell.scss';
import classnamesBind from 'classnames/bind';
const classNames = classnamesBind.bind(styles);

/**
 * Props for calendar all day well
 */
export interface CalendarExpandableAllDayWellProps {
    isSplitView: boolean;
    allDayContent: React.ReactNode;
    visibleRowCount: number;
    allDayUncollapsedRowCount: number;
    allDayRef?: React.Ref<HTMLDivElement>;
    children?: React.ReactNode;
    handlers?: React.DOMAttributes<HTMLDivElement>;
}

/**
 * The control is showing calendar all day events
 */
export default observer(function CalendarExpandableAllDayWell(
    props: CalendarExpandableAllDayWellProps
) {
    const {
        isSplitView,
        children,
        allDayContent,
        handlers,
        allDayRef,
        visibleRowCount,
        allDayUncollapsedRowCount,
    } = props;
    const collapsedMaxHeight = getAllDayWellHeight();

    const collapsedMaxRows = Math.floor(collapsedMaxHeight / SurfaceConstants.AllDayRowHeight);
    const numberOfPlaceholderRows = Math.min(visibleRowCount, collapsedMaxRows);

    const variableWellHeight = visibleRowCount
        ? SurfaceConstants.AllDayRowHeight * visibleRowCount +
          SurfaceConstants.allDayWellHeightBuffer
        : visibleRowCount;
    const placeholderHeight = numberOfPlaceholderRows
        ? SurfaceConstants.AllDayRowHeight * numberOfPlaceholderRows +
          SurfaceConstants.allDayWellHeightBuffer
        : numberOfPlaceholderRows;

    const isExpanded = visibleRowCount > numberOfPlaceholderRows;
    const forceResizedView = isSplitView && !isExpanded;

    const allDayPanelStyle: React.CSSProperties =
        variableWellHeight && !forceResizedView
            ? {
                  height: toPixelString(variableWellHeight),
              }
            : {};

    const placeholderStyle: React.CSSProperties = variableWellHeight
        ? {
              height: toPixelString(placeholderHeight),
          }
        : {};

    return (
        <div className={styles.root}>
            <div
                {...{ [SurfaceConstants.allDayPlaceholderAttributeName]: true }}
                style={placeholderStyle}
                className={styles.allDayPlaceholder}
            />
            <div
                className={classNames(styles.allDayPanelWrapper, {
                    [styles.allDayPanelWrapperExpanded]: isExpanded,
                })}>
                <div className={classNames({ [styles.forceResizedView]: forceResizedView })}>
                    <div
                        {...handlers}
                        ref={allDayRef}
                        className={classNames(
                            styles.allDayPanel,
                            styles.allDayPanelExpandable,
                            'customScrollBar',
                            {
                                [styles.allDayPanelExpanded]: isExpanded,
                            }
                        )}
                        style={allDayPanelStyle}
                        key="alldaypanel_1"
                        data-max-height={
                            (allDayUncollapsedRowCount + 1) * SurfaceConstants.AllDayRowHeight +
                            SurfaceConstants.allDayWellHeightBuffer
                        }
                        data-is-scrollable={true}>
                        {children}
                        <div
                            className={classNames(styles.allDayPanelRenderStyleWrapper, {
                                [styles.allDayPanelRenderStyleWrapperForceResize]: forceResizedView,
                            })}>
                            <div className={styles.renderAllDayPanel}>{allDayContent}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});
